import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import { useDocChatCapabilities } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatCapabilities'
import { hasDocChatCapability } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatCapabilities'
import { useKnowledgeDocumentUXContextOptional } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import type { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import { createArtifactTab } from 'features/knowledgeDocumentViewer/utils/tabStateUrlHelpers'
import { useCallback } from 'react'

import { useAgenticChatArtifactPanel } from './useAgenticChatArtifactPanel'

export interface ArtifactTarget {
  artifact_id: string
  title: string
}

/** The open tab currently showing this artifact, if any. */
export function findOpenArtifactTab(
  tabs: readonly DocumentTab[],
  artifactId: string
): DocumentTab | undefined {
  return tabs.find((tab) => {
    const item = tab.history[tab.currentIndex]
    return item?.type === 'artifact' && item.artifactId === artifactId
  })
}

/** Open a produced document beside the chat or as a full-width workspace tab. */
export function useArtifactActions(): {
  canUseArtifacts: boolean
  openInPanel: (target: ArtifactTarget) => void
  openInTab: (target: ArtifactTarget) => void
} {
  const { openArtifact } = useAgenticChatArtifactPanel()
  const { siteId } = useAdvancedSearchUX()
  const { data: capabilityManifest } = useDocChatCapabilities(siteId)
  const canUseArtifacts = hasDocChatCapability(capabilityManifest, 'artifacts')
  const ux = useKnowledgeDocumentUXContextOptional()
  const addTab = ux?.addTab
  const openTabs = ux?.openTabs
  const setActiveTabId = ux?.setActiveTabId

  const openInPanel = useCallback(
    (target: ArtifactTarget) => {
      if (canUseArtifacts) openArtifact(target.artifact_id)
    },
    [canUseArtifacts, openArtifact]
  )

  const openInTab = useCallback(
    (target: ArtifactTarget) => {
      if (!canUseArtifacts) return
      const existing = findOpenArtifactTab(openTabs ?? [], target.artifact_id)
      if (existing) {
        setActiveTabId?.(existing.id)
        return
      }
      addTab?.(
        createArtifactTab({
          artifactId: target.artifact_id,
          title: target.title,
        })
      )
    },
    [addTab, canUseArtifacts, openTabs, setActiveTabId]
  )

  return { canUseArtifacts, openInPanel, openInTab }
}
